import React from "react";
import DocumentTitle from "react-document-title";
import { useParams } from "react-router-dom";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { ContractOnboardingStrings } from "@/screen/Map/contractOnboarding.strings";
import { useContractStepGuard } from "@/screen/ContractManagement/hooks/useContractStepGuard";
import { useIntervenerSelectionController } from "@/screen/ContractManagement/Steps/IntervenerSelection/useIntervenerSelectionController";
import IntervenerSelectionTable from "@/screen/ContractManagement/Steps/IntervenerSelection/IntervenerSelectionTable";
import * as S from "@/screen/ContractManagement/Steps/IntervenerSelection/IntervenerSelectionStyles";

const IntervenerSelectionScreen: React.FC = () =>
{
    const { savingsPlanId } = useParams<{ savingsPlanId: string }>();
    const guard = useContractStepGuard(Number(savingsPlanId), ["2.2", "2.4.2"]);
    const controller = useIntervenerSelectionController(!guard.isChecking, guard.activeStepKey);
    const strings = ContractOnboardingStrings;

    if(guard.isChecking)
    {
        return(
            <DocumentTitle title="IronIA - Seleccionar intervinientes">
                <S.PageRoot>
                    <S.SpinnerContainer>
                        <Spinner size={SpinnerSize.large} />
                    </S.SpinnerContainer>
                </S.PageRoot>
            </DocumentTitle>
        );
    }

    return(
        <DocumentTitle title="IronIA - Seleccionar intervinientes">
            <S.PageRoot>
                <S.PageTitle>
                    {strings.intervenerSelectionTitle}
                </S.PageTitle>
                <S.Description>
                    {strings.intervenerSelectionDescription}
                </S.Description>
                <S.OwnerHint>
                    {strings.intervenerSelectionOwnerHint}
                </S.OwnerHint>

                {controller.isLoading &&
                (
                    <S.SpinnerContainer>
                        <Spinner size={SpinnerSize.large} />
                    </S.SpinnerContainer>
                )}

                {!controller.isLoading && controller.userIdentities.length > 0 &&
                (
                    <IntervenerSelectionTable
                        userIdentities={controller.userIdentities}
                        hasAnyMinor={controller.hasAnyMinor}
                        notifiedOwners={controller.notifiedOwners}
                        notifyingIdentityId={controller.notifyingIdentityId}
                        isChecked={controller.isChecked}
                        isRelationLocked={controller.isRelationLocked}
                        onRelationChange={controller.onRelationChange}
                        onNotifyOwner={controller.onNotifyOwner}
                    />
                )}
                {controller.errorMessage &&
                (
                    <S.ErrorText>
                        {controller.errorMessage}
                    </S.ErrorText>
                )}
                {controller.errorMessage === strings.intervenerSelectionNotReady &&
                (
                    <S.GoToIdentitiesWrap>
                        <S.PrimaryButton onClick={controller.onGoToIdentities}>
                            {strings.intervenerSelectionGoToIdentities}
                        </S.PrimaryButton>
                    </S.GoToIdentitiesWrap>
                )}
                {!controller.errorMessage && controller.validationError &&
                (
                    <S.ErrorText>
                        {controller.validationError}
                    </S.ErrorText>
                )}

                <S.ActionsRow>
                    <S.SecondaryButton
                        disabled={controller.isSubmitting}
                        onClick={controller.onBackToMap}
                    >
                        {strings.intervenerSelectionBack}
                    </S.SecondaryButton>
                    <S.PrimaryButton
                        disabled={!controller.canSubmit}
                        onClick={() =>
                        {
                            void controller.onSubmit();
                        }}
                    >
                        {controller.isSubmitting &&
                        (
                            <S.ButtonSpinner size={SpinnerSize.small} />
                        )}
                        {strings.intervenerSelectionContinue}
                    </S.PrimaryButton>
                </S.ActionsRow>
            </S.PageRoot>
        </DocumentTitle>
    );
};

export default IntervenerSelectionScreen;
